"use client"

import * as React from "react"
import * as AvatarPrimitive from "@radix-ui/react-avatar"

import { cn } from "@/lib/utils"

function Avatar({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root>) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(
        "relative flex size-8 shrink-0 overflow-hidden rounded-full",
        className
      )}
      {...props}
    />
  )
}

function AvatarImage({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn("aspect-square size-full", className)}
      {...props}
    />
  )
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        "bg-muted flex size-full items-center justify-center rounded-full",
        className
      )}
      {...props}
    />
  )
}

type UserAvatarProps = Omit<
  React.ComponentProps<typeof AvatarPrimitive.Root>,
  "children"
> & {
  /** Profile image URL. When omitted, only the fallback is shown. When set, Radix shows the fallback if the image fails to load (e.g. 404). */
  photoSrc?: string | null
  alt?: string
  fallback: React.ReactNode
  fallbackClassName?: string
}

function UserAvatar({
  className,
  photoSrc,
  alt = "",
  fallback,
  fallbackClassName,
  ...props
}: UserAvatarProps) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(
        "relative flex size-8 shrink-0 overflow-hidden rounded-full",
        className
      )}
      {...props}
    >
      {photoSrc ? (
        <AvatarImage src={photoSrc} alt={alt} />
      ) : null}
      <AvatarFallback className={fallbackClassName}>{fallback}</AvatarFallback>
    </AvatarPrimitive.Root>
  )
}

export { Avatar, AvatarImage, AvatarFallback, UserAvatar }
